import { RouteRecordRaw } from 'vue-router';

/**
 * 建议：路由 path 路径与文件夹名称相同，找文件可浏览器地址找，方便定位文件位置
 *
 * 路由meta对象参数说明
 * meta: {
 *      title:          菜单栏及 tagsView 栏、菜单搜索名称（国际化）
 *      isLink：        是否超链接菜单，开启外链条件，`1、isLink: 链接地址不为空 2、isIframe:false`
 *      isHide：        是否隐藏此路由
 *      isKeepAlive：   是否缓存组件状态
 *      isAffix：       是否固定在 tagsView 栏上
 *      isIframe：      是否内嵌窗口，开启条件，`1、isIframe:true 2、isLink：链接地址不为空`
 *      roles：         当前路由权限标识，取角色管理。控制路由显示、隐藏。超级管理员：admin 普通角色：common
 *      icon：          菜单、tagsView 图标，阿里：加 `iconfont xxx`，fontawesome：加 `fa xxx`
 * }
 */

/**
 * 定义动态路由
 * 前端添加路由，请在顶级节点的 `children 数组` 里添加
 * @description 未开启 isRequestRoutes 为 true 时使用（前端控制路由），开启时第一个顶级 children 的路由将被替换成接口请求回来的路由数据
 * @description 各字段请查看 `/@/views/system/menu/component/addMenu.vue 下的 ruleForm`
 * @returns 返回路由菜单数据
 */
export const dynamicRoutes = [
    {
        path: '/',
        name: '首页',
        component: () => import('@/layout/index.vue'),
        redirect: '/home',
        meta: {
            isKeepAlive: true,
            title: '首页',
        },
        children: [
            {
                path: '/home',
                name: '首页',
                component: () => import('@/views/home/index.vue'),
                meta: {
                    title: '首页',
                    isLink: '',
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: true,
                    isIframe: false,
                },
            },
        ],
    },
];

/**
 * 定义404、401界面
 * @link 参考：https://next.router.vuejs.org/zh/guide/essentials/history-mode.html#netlify
 */
export const notFoundAndNoPower = [
    {
        path: '/:path(.*)*',
        name: 'notFound',
        component: () => import('@/views/error/404.vue'),
        meta: {
            title: '404',
            isHide: true,
        },
    },
    {
        path: '/401',
        name: 'noPower',
        component: () => import('@/views/error/401.vue'),
        meta: {
            title: '401',
            isHide: true,
        },
    },
];

/**
 * 定义静态路由（默认路由）
 * 此路由不要动，前端添加路由的话，请在 `dynamicRoutes 数组` 中添加
 * @description 前端控制直接改 dynamicRoutes 中的路由，后端控制不需要修改，请求接口路由数据时，会覆盖 dynamicRoutes 第一个顶级 children 的内容（全屏，不包含 layout 中的路由出口）
 * @returns 返回路由菜单数据
 */
export const staticRoutes = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/login/index.vue'),
        meta: {
            title: '登录',
        },
    },
    {
        path: '/invoice',
        name: 'invoice',
        redirect: '/invoice/index',
        component: () => import('@/views/invoice/index.vue'),
        meta: {
            title: '发票',
        },
        children: [
            {
                path: 'index',
                name: 'invoice_home',
                component: () => import('@/views/invoice/home.vue'),
                meta: {
                    title: '发票',
                },
            },
            {
                path: 'list',
                name: 'invoice_list',
                component: () => import('@/views/invoice/list.vue'),
                meta: {
                    title: '发票',
                },
            },
            {
                path: 'form',
                name: 'invoice_form',
                component: () => import('@/views/invoice/form.vue'),
                meta: {
                    title: '领取发票',
                },
            },
        ],
    },
    {
        path: '/coupons',
        name: 'coupons',
        redirect: '/coupons/coupons',
        component: () => import('@/views/coupons/record.vue'),
        meta: {
            title: '车主买券',
        },
        children: [
            {
                path: 'record',
                name: 'coupon_record',
                component: () => import('@/views/coupons/record.vue'),
                meta: {
                    title: '买券记录',
                },
            },
        ],
    },
    {
        path: '/chat/knowledgeItem',
        name: 'chat_knowledgeItem',
        component: () => import('@/views/chat/knowledgeBase/item.vue'),
        meta: {
            title: '条目管理',
            isHide: true,
        },
    },
    {
        path: '/chat/h5',
        name: 'chat_h5',
        component: () => import('@/views/chat/h5/index.vue'),
        meta: {
            title: '智能客服',
            isHide: true,
        },
    },
];
